import Alert, { AlertProps } from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";

enum IcpBannerStatus {
  AVAILABLE_ICP = "available-icp",
  APPLIED_ICP = "applied-icp",
  UPDATE_ICP = "update-icp",
}

type IcpBannerStatusMap = {
  [key in IcpBannerStatus]: {
    title: string;
    description: string;
    severity: AlertProps["severity"];
    border: string;
    bgcolor: string;
  };
};

interface Props {
  configType: "SAM" | "SOM";
  isIcpAppliedToConfig: boolean;
  isNewIcpAvailable: boolean;
  bannerBody: React.ReactNode;
  overrideDataAction: () => void;
  mergeDataAction?: () => void;
  isMergeDisabled?: boolean;
}

export function IcpDataBanner(props: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  function handleOverrideData() {
    props.overrideDataAction();
    setIsExpanded(false);
  }

  function handleMergeData() {
    props.mergeDataAction?.();
    setIsExpanded(false);
  }

  const icpBannerStatus = useMemo(() => {
    if (props.isIcpAppliedToConfig) {
      return props.isNewIcpAvailable
        ? IcpBannerStatus.UPDATE_ICP
        : IcpBannerStatus.APPLIED_ICP;
    }
    return IcpBannerStatus.AVAILABLE_ICP;
  }, [props.isIcpAppliedToConfig, props.isNewIcpAvailable]);

  const icpBannerStatusMap: IcpBannerStatusMap = {
    [IcpBannerStatus.AVAILABLE_ICP]: {
      title: "Customer Profile Data Available",
      description: `You have available Customer Profile. Select ${props.configType === "SAM" ? "MERGE or " : ""}OVERWRITE to update the data into your ${props.configType}, or continue with manual input. You can modify any data you import.`,
      severity: "info",
      border: "1px solid #e0e0e0",
      bgcolor: "#e5f6fd",
    },
    [IcpBannerStatus.APPLIED_ICP]: {
      title: "Customer Profile Data Copied",
      description:
        "Your Customer Profile Data has been copied into this configuration.",
      severity: "success",
      border: "1px solid #e0e0e0",
      bgcolor: "#daf7e1",
    },
    [IcpBannerStatus.UPDATE_ICP]: {
      title: "Customer Profile Data Updated",
      description: `You have updated Customer Profile data. Select ${props.configType === "SAM" ? "MERGE or " : ""}OVERWRITE to update the data into your ${props.configType}, or continue with manual input. You can modify any data you import.`,
      severity: "warning",
      border: "1px solid #e0e0e0",
      bgcolor: "#fff4e5",
    },
  };

  return (
    <>
      <Alert
        severity={icpBannerStatusMap[icpBannerStatus].severity}
        sx={{
          borderBottomLeftRadius: isExpanded ? 0 : undefined,
          borderBottomRightRadius: isExpanded ? 0 : undefined,
          borderBottom: isExpanded ? "none" : undefined,
          bgcolor: icpBannerStatusMap[icpBannerStatus].bgcolor,
        }}
        action={
          <Button
            id={`${props.configType}-config-view`}
            variant="outlined"
            size="small"
            color="secondary"
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              fontSize: "12px",
              lineHeight: 1.5,
              whiteSpace: "nowrap",
              minWidth: "fit-content",
            }}
          >
            {isExpanded ? "hide" : "view"}
          </Button>
        }
      >
        <AlertTitle>{icpBannerStatusMap[icpBannerStatus].title}</AlertTitle>
        <Typography variant="body2" sx={{ fontSize: "11px" }}>
          {icpBannerStatusMap[icpBannerStatus].description}
        </Typography>
      </Alert>

      <Collapse in={isExpanded} timeout="auto">
        <Box
          sx={{
            border: icpBannerStatusMap[icpBannerStatus].border,
            borderTop: "none",
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
          }}
        >
          <Divider />
          <Box
            sx={{
              p: 2,
            }}
          >
            {props.bannerBody}
          </Box>
          <Divider />
          <Box display="flex" justifyContent="flex-end" padding={1}>
            <Button
              id={`${props.configType}-config-override`}
              variant="text"
              color="secondary"
              size="small"
              sx={{ fontSize: "12px", paddingRight: 2 }}
              onClick={handleOverrideData}
            >
              overwrite
            </Button>
            {props.configType === "SAM" ? (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                disabled={props.isMergeDisabled}
                sx={{ fontSize: "12px" }}
                onClick={handleMergeData}
              >
                merge
              </Button>
            ) : undefined}
          </Box>
          {props.isMergeDisabled && (
            <Box sx={{ p: 1 }}>
              <Typography variant="body2" color="error" textAlign="center">
                Note: Currently applied filters conflict with customer profile
                analysis. Merge not available
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </>
  );
}
